import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import scrollTo from 'gatsby-plugin-smoothscroll';
import { ExpendedReport } from './ExpandedReport';
import { CustomBlockContent } from './CustomBlockContent';
import { debounce } from 'lodash';

interface ShadowedProps {
  imageUrl: string;
  background: string;
}

interface ChapterProps {
  chapter: any;
  chapterNumber: number;
  chapterRef?: any;
  setOpenChapter: (e: number) => void;
  openChapter: boolean;
  open: Array<boolean>;
  setOpen: any;
}

export const Chapter: FC<ChapterProps> = ({
  chapter,
  chapterNumber,
  chapterRef,
  setOpenChapter,
  openChapter,
  open,
  setOpen,
}) => {
  const { title, _rawText, chapterSections, backgroundImage, opacity } =
    chapter;

  const [activeChapterSection, setActiveChapterSection] = useState(0);
  const [sectionChanged, setSectionChanged] = useState(false);

  const toggleExpended = debounce(() => {
    setOpenChapter(chapterNumber);

    if (open[chapterNumber - 1] === true)
      return setOpen(new Array(open.length).fill(false));
    open = new Array(open.length).fill(false);

    open[chapterNumber - 1] = true;
    setOpen(open);
    scrollToTopOfChapter();
  }, 300);

  const scrollToTopOfChapter = debounce(() => {
    open[chapterNumber - 1] === true && scrollTo(`#chapter${chapterNumber}`);
  }, 300);

  const changeActiveChapterSection = (id: number) => {
    if (id === activeChapterSection) return;
    if (id === chapterSections.length) {
      setOpen(new Array(open.length).fill(false));
      setOpenChapter(0);
      setActiveChapterSection(0);
      scrollToTopOfChapter();
      return;
    }
    setActiveChapterSection(id);
    setSectionChanged(!sectionChanged);
    scrollTo(`#chapter${chapterNumber}`);
  };

  const opacityDecimals = opacity ? opacity / 10 : 0.5;
  const opacityBackground = `rgba(0,0,0, ${opacityDecimals})`;
  const chapterButtonText = open[chapterNumber - 1]
    ? 'Close Chapter'
    : 'Expand Chapter';

  return (
    <StyledReport
      imageUrl={chapter.backgroundImage.asset.url}
      ref={chapterRef}
      background={opacityBackground}
    >
      <div className="background-container">
        <div className="overlay">
          <div
            className="background"
            style={{ backgroundImage: `url(${backgroundImage.asset.url})` }}
          >
            <div className="box-center">
              <div className="content">
                <h3 className="second-title">Chapter {chapterNumber}</h3>
                <h1 className="main-title">{title}</h1>
                <CustomBlockContent blocks={_rawText} />
                <div className="button-container">
                  <button
                    onClick={toggleExpended}
                    className={
                      open[chapterNumber - 1] === true
                        ? 'white-transparent'
                        : 'red'
                    }
                  >
                    {chapterButtonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ExpendedReport
        open={open[chapterNumber - 1]}
        openChapter={openChapter}
        chapterNumber={chapterNumber}
        sections={chapterSections}
        activeSection={activeChapterSection}
        changeActiveSection={changeActiveChapterSection}
        sectionChanged={sectionChanged}
        mainTitle={title}
      />
    </StyledReport>
  );
};

const StyledReport = styled.div<ShadowedProps>`
  transition: max-height 0.25s ease-out;
  .background-container {
  position: relative;
  height: 100vh;
  z-index: 100;

  .background {
    background-image: url(${(p) => p.imageUrl});
    position: relative;
    height: 100vh;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    justify-content: center;
    align-items: center;
      &:before {
          content: "";
          position: absolute;
          left: 0; right: 0;
          top: 0; bottom: 0;
          background: ${(p) => p.background}
          
      }
    }

    .box-center {
      display: flex;
      width: 50%;
      align-items: center;
      justify-content: center;
      padding: 35px;
      opacity: 0.9;
      margin-bottom: 100px;
      @media (max-width: 868px) {
        width: 70%;
      }

      @media (max-width: 500px) {
        width: 90%;
        padding: 10px;
      }

      .content {
        display: flex;
        flex-wrap: wrap;
        text-align: center;
        justify-content: center;
        color: var(--white);

        h1 {
          min-width: 100%;
          margin-bottom: 0px !important;
          @media (max-width: 868px) {
              margin-top: 8px;
              font-size: 2.5em !important;
            }
        }
        h3 {
            font-family: Agaramond_Italic;
            font-size: 2em !important;
            @media (max-width: 868px) {
              font-size: 1.7em !important;
            }
        }

        p {
          font-size: 1em;
          font-style: italic;
          margin: 20px 0 35px 0;
          @media (max-width: 868px) {
            font-size: 0.8em;
            width: 100%;
            font-weight: bold;
            margin: 20px 0;
          }

          @media (max-width: 600px) {
            margin: 15px 0;
          }
        }

        .button-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          width: 60%;
          button {
            text-transform: uppercase;
            min-width: 100%;
            font-size: 1em;
          }

          .download-button {
            background: transparent;
            color: var(--red);
            box-shadow: 0;
          }
        }
      }
    }
  }
}


`;
